/**
 * AASB 102 — Inventories. Rulepack v1.0.0.
 * Lower of cost / NRV check; slow-moving and obsolete-stock flagging.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb102-1.0.0';
const fb = findingBuilder('AASB_102' as any, VERSION);

const HMAP = makeHeaderMap([
  ['inventory_id', 'inventory id', 'sku', 'item id', 'item code', 'stock id'],
  ['description', 'description', 'item description', 'name'],
  ['category', 'category', 'class', 'group'],
  ['cost_per_unit', 'cost per unit', 'unit cost', 'cost'],
  ['quantity_on_hand', 'quantity', 'qty', 'qty on hand', 'stock on hand', 'units', 'quantity on hand', 'on hand'],
  ['nrv_per_unit', 'nrv', 'nrv per unit', 'net realisable value', 'expected sale price'],
  ['carrying_amount', 'carrying amount', 'book value', 'inventory value'],
  ['last_movement_date', 'last movement', 'last sale', 'last transaction'],
  ['as_at_date', 'as at', 'as at date', 'reporting date'],
]);

const REQUIRED = ['inventory_id', 'cost_per_unit', 'quantity_on_hand'];

export function detectAASB102(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB102(headers: string[], data: CellValue[][], asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_102' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('inventory_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB102.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const cost = parseNumber(get('cost_per_unit'));
    const qty = parseNumber(get('quantity_on_hand'));
    const nrv = idx.nrv_per_unit !== undefined ? parseNumber(get('nrv_per_unit')) : null;
    const carrying = idx.carrying_amount !== undefined ? parseNumber(get('carrying_amount')) : null;
    const lastMove = idx.last_movement_date !== undefined ? parseDate(get('last_movement_date')) : null;

    if (!(cost !== null && cost > 0)) { findings.push(fb.build('AASB102.R02', 'error', 'Cost per unit must be positive', `Item ${id}: cost ${cost}.`, { rowId: id })); return; }
    if (!(qty !== null && qty >= 0)) { findings.push(fb.build('AASB102.R03', 'error', 'Quantity must be non-negative', `Item ${id}: qty ${qty}.`, { rowId: id })); return; }

    // R04 NRV write-down check
    if (nrv !== null && nrv < cost) {
      const writeDown = (cost - nrv) * qty;
      findings.push(fb.build('AASB102.R04', 'warning', 'NRV below cost — write-down required',
        `Item ${id}: NRV $${fmt(nrv)} < cost $${fmt(cost)}. Required write-down on ${qty} units = $${fmt(writeDown)}. AASB 102.9 requires inventories to be measured at lower of cost and NRV.`,
        { rowId: id, evidence: { cost, nrv, qty, writeDown }, suggestedAdjustment: { field: 'cost_per_unit', from: cost, to: nrv, rationale: 'Apply lower-of-cost/NRV.' } }));
    }

    // R05 carrying-amount internal consistency
    if (carrying !== null && nrv !== null) {
      const expected = Math.min(cost, nrv) * qty;
      const tol = Math.max(50, 0.005 * (cost * qty));
      if (Math.abs(carrying - expected) > tol) {
        findings.push(fb.build('AASB102.R05', 'warning', 'Carrying amount does not match lower of cost/NRV × qty',
          `Item ${id}: reported $${fmt(carrying)}, expected $${fmt(expected)} (lower of cost/NRV × qty).`,
          { rowId: id, evidence: { carrying, expected }, suggestedAdjustment: { field: 'carrying_amount', from: carrying, to: +expected.toFixed(2), rationale: 'Lower of cost/NRV × quantity.' } }));
      }
    }

    // R06 slow-moving flag
    if (lastMove) {
      const months = (asAt.getTime() - lastMove.getTime()) / (30.44 * 86400000);
      if (months >= 24) {
        findings.push(fb.build('AASB102.R06', 'warning', 'Likely obsolete — no movement in 24+ months',
          `Item ${id}: last movement ${lastMove.toISOString().slice(0, 10)} (${Math.round(months)} months ago). Investigate for impairment / write-off.`,
          { rowId: id, evidence: { months } }));
      } else if (months >= 12) {
        findings.push(fb.build('AASB102.R06', 'info', 'Slow-moving — no movement in 12+ months',
          `Item ${id}: last movement ${Math.round(months)} months ago. Consider NRV reassessment.`,
          { rowId: id, evidence: { months } }));
      }
    }
  });

  return {
    summary: { standard: 'AASB_102' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
